import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { get, post, postStream } from '../api';
import { getLang, msg, tr } from '../i18n';
import { useSession, useTenant } from '../session';
import { Button, Icons, friendlyError } from '../ui';
import { ProposalCard, type Proposal } from './proposal';
import { AssistantText } from './text';

/**
 * The assistant panel: ask a question in ordinary words, get an answer, and — when the answer is an
 * action — a card to confirm (docs/assistant.md).
 *
 * The panel deliberately shows its work. Every look-up the assistant makes appears as a line the person
 * can read, so "it says my laptop backed up on Tuesday" is always traceable to something it actually
 * checked rather than something it produced.
 */

interface Turn {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** Look-ups made while producing this answer, in order. */
  tools?: string[];
  proposal?: Proposal;
  failed?: string;
}

/** Plain-language labels for the look-ups, so the trace reads like a sentence. */
const TOOL_LABELS: Record<string, string> = {
  overview: msg('Checked how your organization is doing'),
  list_computers: msg('Looked at your computers'),
  computer_details: msg('Looked at that computer'),
  list_backup_runs: msg('Checked recent backups'),
  backup_run_details: msg('Read that backup in full'),
  computer_activity: msg('Read what the computer reported'),
  list_alerts: msg('Checked your alerts'),
  list_backup_plans: msg('Looked at your backup plans'),
  storage_usage: msg('Checked your storage'),
  list_saved_backups: msg('Listed the saved backups'),
  find_file: msg('Searched a saved backup'),
  browse_backup: msg('Opened a folder in a saved backup'),
  file_versions: msg('Looked for earlier versions'),
  estimate_recovery: msg('Worked out how much there is to get back'),
  list_recoveries: msg('Checked your recoveries'),
  recovery_details: msg('Looked at that recovery'),
  recovery_problems: msg('Checked what did not arrive'),
  skipped_files: msg('Checked which files were left out'),
  recently_deleted: msg('Looked for deleted files'),
  search_all_computers: msg('Searched every computer'),
  activity_log: msg('Read the activity record'),
  who_can_help: msg('Checked who can help'),
  my_notifications: msg('Read your notifications'),
  period_summary: msg('Counted up the month'),
  plan_details: msg('Read that backup plan'),
  back_up_now: msg('Prepared a backup for you to confirm'),
  cancel_backup: msg('Prepared that for you to confirm'),
  pause_backup_plan: msg('Prepared that for you to confirm'),
  resume_backup_plan: msg('Prepared that for you to confirm'),
  settle_alert: msg('Prepared that for you to confirm'),
  recover_files: msg('Prepared the recovery for you to confirm'),
  cancel_recovery: msg('Prepared that for you to confirm'),
  retry_recovery: msg('Prepared that for you to confirm'),
  keep_backup: msg('Prepared that for you to confirm'),
  pause_cleanup: msg('Prepared that for you to confirm'),
  change_plan: msg('Prepared that for you to confirm'),
  copy_plan_for_computers: msg('Prepared that for you to confirm'),
  move_computers_to_plan: msg('Prepared that for you to confirm'),
};

const SUGGESTIONS = [
  msg('Is everything backed up?'),
  msg('Why did last night’s backup fail?'),
  msg('I deleted a file — can you find it?'),
  msg('When did my laptop last back up?'),
  msg('Is my email backed up?'),
];

// ───────────────────────── the panel ─────────────────────────

function Panel({ seedDeviceId, onClose }: { seedDeviceId?: string; onClose: () => void }) {
  const t = useTenant();
  const qc = useQueryClient();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const conversationId = useRef<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const abort = useRef<AbortController | null>(null);

  const status = useQuery({ queryKey: ['assistant', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}/assistant`) });

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' });
  }, [turns]);
  useEffect(() => () => abort.current?.abort(), []);

  const ask = useCallback(
    async (message: string) => {
      if (!message.trim() || busy) return;
      setBusy(true);
      setDraft('');
      const answer: Turn = { id: `a-${Date.now()}`, role: 'assistant', text: '', tools: [] };
      setTurns((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', text: message }, answer]);
      const patch = (fn: (turn: Turn) => Turn) => setTurns((prev) => prev.map((x) => (x.id === answer.id ? fn(x) : x)));

      try {
        conversationId.current ??= (await post(`/api/t/${t.tenantId}/assistant/conversations`, seedDeviceId ? { deviceId: seedDeviceId } : {})).id;
        abort.current = new AbortController();
        await postStream(
          `/api/t/${t.tenantId}/assistant/conversations/${conversationId.current}/messages`,
          { message },
          (e: any) => {
            if (e.type === 'text') patch((x) => ({ ...x, text: x.text + e.delta }));
            else if (e.type === 'tool' && e.state === 'running') patch((x) => ({ ...x, tools: [...(x.tools ?? []), e.name] }));
            else if (e.type === 'proposal') patch((x) => ({ ...x, proposal: e.proposal }));
            else if (e.type === 'error') patch((x) => ({ ...x, failed: e.message }));
          },
          // The assistant writes its own sentences, so they cannot go through tr(): the server has to
          // tell the model which language to answer in, and this is where that choice is known.
          { signal: abort.current.signal, headers: { 'x-zerogap-language': getLang() } },
        );
      } catch (e) {
        patch((x) => ({ ...x, failed: friendlyError(e) }));
      } finally {
        setBusy(false);
        // A confirmed action changes what the rest of the portal is showing.
        qc.invalidateQueries();
      }
    },
    [busy, qc, seedDeviceId, t.tenantId],
  );

  // Opened from a computer's page: start with that computer in view rather than a blank box.
  useEffect(() => {
    if (seedDeviceId && turns.length === 0) void ask(tr('Tell me about this computer.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedDeviceId]);

  const avail = status.data;
  return (
    <aside className="assist" role="complementary" aria-label={tr('Ask ZeroGap')}>
      <header className="assist-head">
        <strong>{tr('Ask ZeroGap')}</strong>
        <button type="button" className="assist-x" onClick={onClose} aria-label={tr('Close')}>×</button>
      </header>

      <div className="assist-body" ref={scroller}>
        {avail && !avail.available ? (
          <Unavailable reason={avail.reason} />
        ) : (
          <>
            {turns.length === 0 && (
              <div className="assist-intro">
                <p>{tr('Ask about your backups in your own words. I can look things up, find a file you have lost, and offer to put it back — you always confirm before anything happens.')}</p>
                <div className="assist-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} type="button" onClick={() => void ask(tr(s))}>{tr(s)}</button>
                  ))}
                </div>
              </div>
            )}
            {turns.map((turn) => (
              <div key={turn.id} className={`assist-turn ${turn.role}`}>
                {turn.role === 'assistant' && !!turn.tools?.length && (
                  <ul className="assist-trace">
                    {turn.tools.map((name, i) => (
                      <li key={`${name}-${i}`}>{tr(TOOL_LABELS[name] ?? msg('Looked something up'))}</li>
                    ))}
                  </ul>
                )}
                {turn.text && <AssistantText text={turn.text} streaming={busy && turn.role === 'assistant'} plain={turn.role === 'user'} />}
                {turn.role === 'assistant' && !turn.text && !turn.failed && busy && <div className="assist-typing" aria-label={tr('Thinking')}><span /><span /><span /></div>}
                {turn.proposal && (
                  <ProposalCard
                    proposal={turn.proposal}
                    onDecided={(state) => {
                      qc.invalidateQueries();
                      if (state === 'done') void ask(tr('Done — what happened?'));
                      // The assistant has been told why it failed; let it explain and offer what to do next.
                      else if (state === 'failed') void ask(tr('It did not work — why, and what can be done?'));
                    }}
                  />
                )}
                {turn.failed && <div className="assist-failed">{turn.failed}</div>}
              </div>
            ))}
          </>
        )}
      </div>

      {avail?.available && (
        <form
          className="assist-ask"
          onSubmit={(e) => {
            e.preventDefault();
            void ask(draft);
          }}
        >
          <label className="sr-only" htmlFor="assist-input">{tr('Your question')}</label>
          <textarea
            id="assist-input"
            rows={2}
            value={draft}
            disabled={busy}
            placeholder={tr('Ask about your backups…')}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void ask(draft);
              }
            }}
          />
          <Button variant="primary" size="sm" type="submit" busy={busy} disabled={!draft.trim()}>{tr('Ask')}</Button>
        </form>
      )}
      <p className="assist-foot small muted">{tr('Answers come from your own backup records. Check anything important on the page it came from.')}</p>
    </aside>
  );
}

function Unavailable({ reason }: { reason?: string }) {
  const t = useTenant();
  if (reason === 'not_enabled')
    return (
      <div className="assist-intro">
        <p>{tr('The assistant is switched off for this organization.')}</p>
        {t.can('settings.manage') ? (
          <p className="small">{tr('An administrator can switch it on in Settings. Using it sends the names of your files and folders — never their contents — to the service that powers it.')}</p>
        ) : (
          <p className="small">{tr('An administrator of your organization can switch it on in Settings.')}</p>
        )}
      </div>
    );
  if (reason === 'no_budget')
    return (
      <div className="assist-intro">
        <p>{tr('This organization has used all its assistant questions for this month.')}</p>
        <p className="small">{tr('Everything else keeps working as normal, and the allowance starts again next month.')}</p>
      </div>
    );
  return (
    <div className="assist-intro">
      <p>{tr('The assistant is not available here.')}</p>
    </div>
  );
}

// ───────────────────────── mounting ─────────────────────────

const Ctx = createContext<{ open: (deviceId?: string) => void; available: boolean }>({ open: () => {}, available: false });

/** `useAssistant().open(deviceId)` from anywhere in the portal, e.g. a computer's page. */
export const useAssistant = () => useContext(Ctx);

export function AssistantProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const t = useTenant();
  const [open, setOpen] = useState<{ deviceId?: string } | null>(null);
  const [seen, setSeen] = useState(0); // remounts the panel for a fresh conversation
  const enabled = !!me && !!t.tenantId;

  const status = useQuery({
    queryKey: ['assistant', t.tenantId],
    queryFn: () => get(`/api/t/${t.tenantId}/assistant`),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
  // The dock is hidden entirely when this installation has no assistant at all: there is nothing an
  // organization could switch on, so offering it would be a dead end.
  const installed = status.data ? status.data.reason !== 'provider_off' : false;

  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null);
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open]);

  return (
    <Ctx.Provider
      value={{
        available: installed && !!status.data?.available,
        open: (deviceId?: string) => {
          setSeen((n) => n + 1);
          setOpen({ deviceId });
        },
      }}
    >
      {children}
      {enabled && installed && (
        <>
          {open && <Panel key={seen} seedDeviceId={open.deviceId} onClose={() => setOpen(null)} />}
          {!open && (
            <button type="button" className="assist-dock" onClick={() => setOpen({})} lang={getLang()}>
              <Icons.chat />
              {tr('Ask ZeroGap')}
            </button>
          )}
        </>
      )}
    </Ctx.Provider>
  );
}
